import type { ConceptEdge } from '@arianna/shared'
import { relationFamily } from './families.js'
import type { ChapterGraph } from './types.js'

export interface RestingRule {
  kMin: number
  kMax: number
  capPerNode: number
}

export const DEFAULT_RESTING_RULE: RestingRule = { kMin: 2, kMax: 4, capPerNode: 2 }

/**
 * Regola 2/4/2N (§ Contracts del task): seleziona gli archi intra a riposo,
 * cioè visibili senza alcuna selezione. Solo archi `scope === 'intra'` con
 * entrambi gli estremi residenti nel capitolo.
 */
export function selectRestingEdges(
  graph: ChapterGraph,
  rule: RestingRule = DEFAULT_RESTING_RULE,
): Set<string> {
  const { kMin, kMax, capPerNode } = rule
  const nodeIds = new Set(graph.nodes.map((n) => n.nodeId))
  const intra = graph.edges.filter(
    (e) =>
      e.scope === 'intra' &&
      nodeIds.has(e.sourceNodeId) &&
      nodeIds.has(e.targetNodeId) &&
      relationFamily(e.relationType) !== null,
  )
  const N = graph.nodes.length

  // 1. Incidenza per nodo residente, ordinata per confidence decrescente poi edgeId crescente.
  const incident = new Map<string, ConceptEdge[]>()
  for (const id of nodeIds) incident.set(id, [])
  for (const e of intra) {
    incident.get(e.sourceNodeId)?.push(e)
    incident.get(e.targetNodeId)?.push(e)
  }
  for (const list of incident.values()) {
    list.sort((a, b) => {
      if (a.confidence !== b.confidence) return b.confidence - a.confidence
      return a.edgeId < b.edgeId ? -1 : a.edgeId > b.edgeId ? 1 : 0
    })
  }

  // 2. floor = unione dei primi kMin di ciascun nodo; candidates = quante volte
  //    un arco è tra i primi kMax di un nodo (0, 1 o 2).
  const floor = new Set<string>()
  const candidates = new Map<string, number>()
  for (const list of incident.values()) {
    for (const e of list.slice(0, kMin)) floor.add(e.edgeId)
    for (const e of list.slice(0, kMax))
      candidates.set(e.edgeId, (candidates.get(e.edgeId) ?? 0) + 1)
  }

  // 3. Visibile = in floor oppure candidato per entrambi gli estremi.
  const visible = new Set<string>()
  for (const e of intra) {
    if (floor.has(e.edgeId) || candidates.get(e.edgeId) === 2) visible.add(e.edgeId)
  }

  // 4. Cap: se troppi visibili, rimuovi dai non-floor per confidence crescente poi edgeId crescente.
  const cap = capPerNode * N
  if (visible.size > cap) {
    const removable = intra
      .filter((e) => visible.has(e.edgeId))
      .sort((a, b) => {
        if (a.confidence !== b.confidence) return a.confidence - b.confidence
        return a.edgeId < b.edgeId ? -1 : a.edgeId > b.edgeId ? 1 : 0
      })
    for (const e of removable) {
      if (visible.size <= cap) break
      if (floor.has(e.edgeId)) continue
      visible.delete(e.edgeId)
    }
  }

  return visible
}
